import { useCallback } from 'react';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';

/**
 * The exact set of keys the catalogs define, as i18next computes it from the module augmentation
 * in resources.ts. Using this rather than `string` is what makes a mistyped `labelKey` a compile
 * error instead of a blank label at runtime.
 */
export type TranslationKey = ParseKeys;

/**
 * Translating API code fields.
 *
 * The backend labels most things in Chinese only: `package_list` returns
 * `{ status: "NEW", status_cn: "新包裹" }` and there is no `status_en`. Across the whole API
 * document there are six `*_cn` fields and exactly one `*_en` twin, so rendering `*_cn` would
 * show Chinese to English and Malay users.
 *
 * The rule is therefore: **key off the code, never off `*_cn`.** A dictionary maps each code to
 * one of our own catalog keys, so the label follows the reader's language rather than the
 * backend's. `*_cn` is ignored entirely — treat it as debugging noise on the wire.
 *
 * Codes only. Never build a dictionary for unbounded data — countries, provinces, cities,
 * channels, warehouse names — which arrives from the API already localised (or not) and changes
 * without a release. Those render exactly as received.
 */
/**
 * `TKey` is deliberately un-defaulted. Giving it a `= string` default, or annotating a registry as
 * a bare `CodeDictionary`, widens `labelKey` back to `string` and the compiler stops checking that
 * the key exists — which defeats the whole translation-coverage guarantee. Registries should be
 * written with `as const` and no type annotation, so the literal keys survive to the `t()` call.
 */
export interface DictionaryEntry<TKey extends TranslationKey> {
  /** The literal value the API sends, e.g. "NEW". */
  readonly code: string;
  /** Key in our own catalogs, checked against the catalogs at compile time. */
  readonly labelKey: TKey;
}

export type CodeDictionary<TKey extends TranslationKey> = readonly DictionaryEntry<TKey>[];

/**
 * The constraint a registry declares itself against:
 *
 *     export const ORDER_STATUS = [...] as const satisfies AnyCodeDictionary;
 *
 * `satisfies` rather than a type annotation is the whole point. An annotation would widen
 * `labelKey` back to `string`; `satisfies` checks every key against the catalogs while leaving the
 * literal types intact. Without it a registry is only validated where it is *used*, so a typo in a
 * dictionary that no page consumes yet would compile silently.
 */
export type AnyCodeDictionary = readonly DictionaryEntry<TranslationKey>[];

/** The entry for a code, or undefined when the backend sends something we don't know about. */
export function findEntry<TKey extends TranslationKey>(
  dictionary: CodeDictionary<TKey>,
  code: string | null | undefined,
): DictionaryEntry<TKey> | undefined {
  if (!code) return undefined;
  return dictionary.find((entry) => entry.code === code);
}

/**
 * Resolves an API code to a label in the active language.
 *
 * Unknown codes fall back to the raw code rather than an empty cell: the status dictionaries are
 * fetched at runtime, so the backend can introduce a value this build has never seen. Showing
 * "REFUNDED" is ugly but honest, and visible enough that someone reports it — which is the point.
 * No dev-only logging here: this package stays free of bundler-specific globals so it can be
 * type-checked on its own.
 */
export function useCodeLabel() {
  const { t } = useTranslation();

  return useCallback(
    <TKey extends TranslationKey>(
      dictionary: CodeDictionary<TKey>,
      code: string | null | undefined,
    ) => {
      const entry = findEntry(dictionary, code);
      return entry ? t(entry.labelKey) : (code ?? '');
    },
    [t],
  );
}
